import { queryKeys } from "@l2a/shared";
import { AlarmClock, FileSpreadsheet, ReceiptIndianRupee, Repeat, Stamp } from "lucide-react";
import { Suspense, lazy } from "react";
import { defineFeature } from "../types";

/**
 * Invoices — GST documents, the paper they turn into, and the receivables they
 * create (roadmap item 4, PRD US-4.1 … US-4.10).
 *
 * `/invoices` is the register, `/invoices/new` and `/invoices/:id/edit` are the
 * editor (a draft is the only editable state), `/invoices/:id` is the issued
 * document, `/invoices/retainer-run` is the monthly ritual and
 * `/invoices/exports` is what goes to the CA. `/i/:token` is the client's
 * login-free view and is deliberately unauthenticated.
 *
 * Every page is lazy: this module costs the entry bundle its icons and its
 * metadata, nothing more.
 */

const InvoiceSettingsPage = lazy(() => import("./settings"));

/**
 * Deliberately plain markup rather than <Skeleton>: the registry imports this
 * module statically, so anything it touches lands in the entry chunk.
 */
function SettingsFallback() {
  return (
    <div className="grid w-full gap-5" role="status" aria-label="Loading invoicing settings">
      <div className="skeleton-shimmer h-6 w-56 rounded-item" />
      <div className="skeleton-shimmer h-56 w-full rounded-card" />
      <div className="skeleton-shimmer h-72 w-full rounded-card" />
    </div>
  );
}

export default defineFeature({
  key: "invoices",
  title: "Invoices",
  entities: ["invoice", "credit_note"],
  routes: [
    { path: "invoices", lazy: () => import("./page") },
    { path: "invoices/new", lazy: () => import("./editor") },
    { path: "invoices/retainer-run", lazy: () => import("./retainer-run") },
    { path: "invoices/exports", lazy: () => import("./exports") },
    { path: "invoices/:id", lazy: () => import("./detail") },
    { path: "invoices/:id/edit", lazy: () => import("./editor") },
    // A credit note is an invoice row with its own doc type, so it is the same page.
    { path: "credit-notes/:id", lazy: () => import("./detail") },
  ],
  publicRoutes: [{ path: "/i/:token", lazy: () => import("./public") }],
  nav: [
    {
      label: "Invoices",
      to: "/invoices",
      icon: ReceiptIndianRupee,
      group: "finance",
      order: 10,
      permission: "invoices:read",
      badgeQueryKey: queryKeys.list("invoice", { status: ["overdue"], limit: 1, offset: 0 }),
      badgeTone: "danger",
      description: "GST invoices, credit notes and receivables",
    },
  ],
  quickCreate: [
    {
      key: "invoices",
      label: "Invoice",
      icon: ReceiptIndianRupee,
      to: "/invoices/new",
      permission: "invoices:write",
      order: 40,
      shortcut: "i",
      description: "Bill a client with GST applied",
    },
  ],
  commands: [
    {
      key: "invoices.overdue",
      label: "Show overdue invoices",
      icon: AlarmClock,
      to: "/invoices?overdue=true",
      group: "actions",
      permission: "invoices:read",
      keywords: ["late", "chase", "unpaid", "receivables", "ageing"],
      description: "Everything past its due date with a balance",
    },
    {
      key: "invoices.drafts",
      label: "Unissued drafts",
      icon: Stamp,
      to: "/invoices?status=draft",
      group: "actions",
      permission: "invoices:read",
      keywords: ["draft", "not issued", "pending", "unbilled"],
    },
    {
      key: "invoices.retainer-run",
      label: "Retainer run",
      icon: Repeat,
      to: "/invoices/retainer-run",
      group: "navigate",
      permission: "invoices:read",
      keywords: ["recurring", "monthly", "retainers", "bulk invoice"],
      description: "This month's retainer invoices, ready to issue",
    },
    {
      key: "invoices.exports",
      label: "Exports for your CA",
      icon: FileSpreadsheet,
      to: "/invoices/exports",
      group: "navigate",
      permission: ["invoices:read", "financials:view"],
      keywords: ["gstr1", "gstr-1", "tally", "accountant", "return", "csv", "xml"],
    },
  ],
  settingsSections: [
    {
      key: "invoicing",
      label: "Invoicing & tax",
      path: "invoicing",
      group: "Finance",
      order: 300,
      icon: ReceiptIndianRupee,
      permission: "invoices:read",
      description: "Books lock, the signature block, and the ledgers your CA imports",
      element: (
        <Suspense fallback={<SettingsFallback />}>
          <InvoiceSettingsPage />
        </Suspense>
      ),
    },
  ],
});
